import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Car,
  Users,
  ImageIcon,
  Search,
  BarChart3,
  Settings,
  Sparkles,
  ChevronLeft,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/i18n";

export function AdminSidebar({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  const { pathname } = useLocation();
  const { t } = useI18n();

  const items = [
    { to: "/admin", label: t.admin.nav.dashboard, icon: LayoutDashboard },
    { to: "/admin/vehicles", label: t.admin.nav.vehicles, icon: Car, badge: "6" },
    { to: "/admin/leads", label: t.admin.nav.leads, icon: Users, badge: "12" },
    { to: "/admin/media", label: t.admin.nav.media, icon: ImageIcon },
    { to: "/admin/seo", label: t.admin.nav.seo, icon: Search },
    { to: "/admin/analytics", label: t.admin.nav.analytics, icon: BarChart3 },
    { to: "/admin/settings", label: t.admin.nav.settings, icon: Settings },
  ] as const;

  return (
    <aside
      className={cn(
        "sticky top-0 hidden h-screen shrink-0 flex-col border-r border-border/60 bg-surface/60 backdrop-blur-xl transition-[width] duration-300 lg:flex",
        collapsed ? "w-[76px]" : "w-[260px]",
      )}
    >
      <div className="flex h-16 items-center justify-between border-b border-border/60 px-4">
        <Link to="/admin" className="flex items-center gap-2.5 overflow-hidden">
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-gradient-to-br from-primary to-primary/40 shadow-[var(--shadow-glow)]">
            <span className="font-display text-sm font-bold text-primary-foreground">M</span>
          </div>
          {!collapsed && (
            <div className="flex min-w-0 flex-col leading-tight">
              <span className="truncate font-display text-sm font-semibold">Meridian</span>
              <span className="truncate font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {t.admin.console}
              </span>
            </div>
          )}
        </Link>
        <button
          onClick={onToggle}
          aria-label={t.admin.collapseSidebar}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground"
        >
          <ChevronLeft className={cn("h-4 w-4 transition", collapsed && "rotate-180")} />
        </button>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto p-3">
        {items.map((item) => {
          const active =
            item.to === "/admin" ? pathname === "/admin" : pathname.startsWith(item.to);
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                "group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all",
                active
                  ? "bg-primary/10 text-foreground shadow-inner"
                  : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                collapsed && "justify-center",
              )}
            >
              {active && (
                <span className="absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r-full bg-primary shadow-[var(--shadow-glow)]" />
              )}
              <Icon className={cn("h-[18px] w-[18px] shrink-0", active && "text-primary")} />
              {!collapsed && (
                <>
                  <span className="flex-1 truncate">{item.label}</span>
                  {"badge" in item && item.badge && (
                    <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">
                      {item.badge}
                    </span>
                  )}
                </>
              )}
            </Link>
          );
        })}
      </nav>

      {!collapsed && (
        <div className="m-3 rounded-xl border border-border/60 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent p-4">
          <div className="flex items-center gap-2 text-xs font-semibold text-primary">
            <Sparkles className="h-3.5 w-3.5" /> {t.admin.conciergeAi}
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">{t.admin.conciergeCopy}</p>
          <button className="mt-3 w-full rounded-md bg-primary py-1.5 text-xs font-semibold text-primary-foreground transition hover:brightness-110">
            {t.admin.enablePreview}
          </button>
        </div>
      )}
    </aside>
  );
}
